import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

export default function NewsArticlePage() {
  const { articleId } = useParams();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    async function fetchArticle() {
      try {
        const { data } = await axios.get(`${API}/api/news/${articleId}`);
        setArticle(data);
      } catch (err) {
        setError('Article not found');
      } finally {
        setLoading(false);
      }
    }
    fetchArticle();
  }, [articleId]);

  if (loading) {
    return (
      <div className="bg-white pt-32 pb-24 min-h-screen">
        <div className="max-w-3xl mx-auto px-6 lg:px-8">
          <div className="animate-pulse">
            <div className="h-4 bg-slate-200 w-32 mb-8" />
            <div className="h-10 bg-slate-200 w-full mb-4" />
            <div className="h-4 bg-slate-200 w-48 mb-8" />
            <div className="h-64 bg-slate-200 mb-8" />
            <div className="space-y-3">
              <div className="h-4 bg-slate-200 w-full" />
              <div className="h-4 bg-slate-200 w-full" />
              <div className="h-4 bg-slate-200 w-3/4" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !article) {
    return (
      <div className="bg-white pt-32 pb-24 min-h-screen">
        <div className="max-w-3xl mx-auto px-6 lg:px-8 text-center">
          <h1 className="font-serif text-3xl text-[#0A192F] mb-4">Article Not Found</h1>
          <p className="text-slate-600 mb-8">The article you're looking for doesn't exist.</p>
          <Link
            to="/news"
            className="inline-flex items-center gap-2 text-[#0A192F] hover:gap-4 transition-all"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to News
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white pt-32 pb-24">
      <article className="max-w-3xl mx-auto px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          {/* Back Link */}
          <Link
            to="/news"
            className="inline-flex items-center gap-2 text-slate-500 hover:text-[#0A192F] transition-colors mb-8 text-sm uppercase tracking-wider"
            data-testid="back-to-news-link"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to News
          </Link>

          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center gap-4 text-xs uppercase tracking-wider text-slate-500 mb-4">
              <span>{article.category}</span>
              <span>·</span>
              <span>{article.date}</span>
            </div>
            <h1 className="font-serif text-3xl lg:text-4xl text-[#0A192F] tracking-tight leading-tight">
              {article.title}
            </h1>
          </div>

          {/* Featured Image */}
          {article.image_url && (
            <div className="mb-10">
              <img
                src={article.image_url}
                alt={article.title}
                className="w-full h-auto"
              />
            </div>
          )}

          {/* Content */}
          <div className="prose prose-lg max-w-none">
            {article.content.split('\n\n').map((paragraph, index) => (
              <p key={index} className="text-slate-600 leading-relaxed mb-6">
                {paragraph.trim()}
              </p>
            ))}
          </div>

          {/* Share / Actions */}
          <div className="mt-16 pt-8 border-t border-slate-200">
            <p className="text-xs uppercase tracking-wider text-slate-500 mb-4">Share this article</p>
            <div className="flex gap-4">
              <button className="px-4 py-2 border border-slate-200 text-sm text-slate-600 hover:border-slate-300 transition-colors">
                LinkedIn
              </button>
              <button className="px-4 py-2 border border-slate-200 text-sm text-slate-600 hover:border-slate-300 transition-colors">
                Twitter
              </button>
              <button className="px-4 py-2 border border-slate-200 text-sm text-slate-600 hover:border-slate-300 transition-colors">
                Email
              </button>
            </div>
          </div>
        </motion.div>
      </article>
    </div>
  );
}
